import type { ReactNode } from 'react';
import { FaAnglesLeft, FaAnglesRight } from 'react-icons/fa6';

export interface SidebarNavItem {
  /** Stable React key. */
  key: string;
  /** Leading icon (top-level items and section headers; usually omitted on submenu leaves). */
  icon?: ReactNode;
  label: ReactNode;
  /** Plain-text tooltip used in collapsed mode; pass the string form of `label`. */
  title?: string;
  /**
   * Link target. On a leaf it is required. On a section header (with `children`)
   * it is optional: when set, the header itself renders through `renderLink`.
   */
  to?: string;
  /** Forwarded by the consumer to its NavLink for exact-path matching. */
  end?: boolean;
  /** Rendered after the label inside the leaf (e.g. an unread-count tag). */
  badge?: ReactNode;
  /** Present → this item is a section header; its children render as a submenu. */
  children?: SidebarNavItem[];
}

export interface SidebarNavProps {
  items: SidebarNavItem[];
  /**
   * The consumer wraps `inner` (the icon+label+badge markup this component
   * builds) in its own navigation element — a react-router <NavLink> that
   * carries the `active` class and the collapsed-mode `title`. Called for every
   * leaf (top-level and submenu); section headers are rendered here, not via
   * renderLink. Keeping routing in the consumer is what lets this component stay
   * free of react-router (platform-agnostic rule).
   */
  renderLink: (item: SidebarNavItem, inner: ReactNode) => ReactNode;
  collapsed: boolean;
  onToggleCollapse: () => void;
  ariaLabel: string;
  expandLabel: string;
  collapseLabel: string;
}

const itemInner = (item: SidebarNavItem): ReactNode => (
  <>
    {item.icon != null && <span className="icon">{item.icon}</span>}
    <span className="nav-label">{item.label}</span>
    {item.badge}
  </>
);

export const SidebarNav = ({
  items,
  renderLink,
  collapsed,
  onToggleCollapse,
  ariaLabel,
  expandLabel,
  collapseLabel,
}: SidebarNavProps) => {
  const toggleLabel = collapsed ? expandLabel : collapseLabel;
  return (
    <aside className={`f2m-sidebar${collapsed ? ' collapsed' : ''}`} aria-label={ariaLabel}>
      <button
        type="button"
        className="f2m-sidebar-toggle"
        onClick={onToggleCollapse}
        aria-label={toggleLabel}
        aria-expanded={!collapsed}
        title={toggleLabel}
      >
        {collapsed ? <FaAnglesRight /> : <FaAnglesLeft />}
      </button>
      <ul>
        {items.map((item) =>
          item.children && item.children.length > 0 ? (
            <li key={item.key}>
              {item.to ? (
                renderLink(item, itemInner(item))
              ) : (
                <span className="section-header" title={item.title}>
                  {item.icon != null && <span className="icon">{item.icon}</span>}
                  <span className="nav-label">{item.label}</span>
                </span>
              )}
              <ul className="submenu">
                {item.children.map((child) => (
                  <li key={child.key}>{renderLink(child, itemInner(child))}</li>
                ))}
              </ul>
            </li>
          ) : (
            <li key={item.key}>{renderLink(item, itemInner(item))}</li>
          ),
        )}
      </ul>
    </aside>
  );
};

export default SidebarNav;
